import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { WORD_API_URL } from '../config';
import axios from 'axios';
import AppShell from './AppShell';
import LetterGrid from './LetterGrid';
import GuessInput from './GuessInput';
import { wordFingerprint, pollForNewWord } from '../utils/wordRefresh';
import {
  assignIndicesForGuess,
  guessFromIndices,
  togglePick,
  readTapHintSeen,
  markTapHintSeen,
  hasFinePointer
} from '../utils/letterPicking';
import './Game.css';

function Game() {
  const [currentWord, setCurrentWord] = useState(null);
  const [shuffledLetters, setShuffledLetters] = useState('');
  const [guess, setGuess] = useState('');
  // Exact tile picks — so duplicate letters gray the tile that was tapped.
  const [pickedIndices, setPickedIndices] = useState([]);
  const [message, setMessage] = useState('');
  const [solved, setSolved] = useState(false);
  const [solvedTime, setSolvedTime] = useState(null);
  const [lastPosition, setLastPosition] = useState(null);
  const [lastScore, setLastScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextWordTime, setNextWordTime] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [refreshingWord, setRefreshingWord] = useState(false);
  const [guessShake, setGuessShake] = useState(false);
  const [showTapHint, setShowTapHint] = useState(() => !readTapHintSeen());
  const wordFingerprintRef = useRef('');
  const guessInputRef = useRef(null);
  const shakeTimerRef = useRef(null);

  const fingerprintFor = useCallback((data) => wordFingerprint(data), []);

  const applyTodayPayload = useCallback((data, { updateNextTime = true } = {}) => {
    setSolved(data.solved);
    setSolvedTime(data.solvedTime);
    setLastPosition(data.position || null);
    setLastScore(typeof data.score === 'number' ? data.score : null);
    if (updateNextTime) {
      setNextWordTime(data.nextWordTime || null);
    }
    setCurrentWord(data.word || null);
    setShuffledLetters(data.shuffledLetters || '');
    setGuess('');
    setPickedIndices([]);
    wordFingerprintRef.current = fingerprintFor(data);
  }, [fingerprintFor]);

  const fetchTodayWord = useCallback(async (options = {}) => {
    try {
      const response = await axios.get(`${WORD_API_URL}/game/today`);
      applyTodayPayload(response.data, options);
      setMessage('');
      return response.data;
    } catch (error) {
      console.error('Error fetching word:', error);
      setMessage('Ошибка загрузки слова');
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyTodayPayload]);

  useEffect(() => {
    fetchTodayWord();
  }, [fetchTodayWord]);

  useEffect(() => {
    return () => {
      if (shakeTimerRef.current) {
        window.clearTimeout(shakeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!nextWordTime) {
      setCountdown('');
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;
    let pollTimer = null;
    let refreshStarted = false;

    const sleep = (ms) =>
      new Promise((resolve) => {
        pollTimer = setTimeout(resolve, ms);
      });

    const refreshAfterPublish = async () => {
      setRefreshingWord(true);
      setCountdown('');
      const previous = wordFingerprintRef.current;

      try {
        // Don't update nextWordTime until the word actually changes —
        // otherwise the countdown jumps ~24h and this effect aborts the poll.
        // Cron may lag a few seconds after the scheduled time.
        // Fetch without applying, so polling doesn't wipe a guess in progress.
        const { data } = await pollForNewWord({
          fetchToday: () =>
            axios
              .get(`${WORD_API_URL}/game/today`)
              .then((response) => response.data)
              .catch(() => null),
          previousFingerprint: previous,
          sleep,
          isCancelled: () => cancelled
        });
        if (data && !cancelled) {
          applyTodayPayload(data, { updateNextTime: true });
          setMessage('');
        }
      } finally {
        if (!cancelled) {
          setRefreshingWord(false);
        }
      }
    };

    const updateCountdown = () => {
      const diff = new Date(nextWordTime).getTime() - Date.now();
      if (diff <= 0) {
        setCountdown('');
        if (!refreshStarted) {
          refreshStarted = true;
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          refreshAfterPublish();
        }
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      const parts = [];
      if (hours > 0) parts.push(`${hours}ч`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes}м`);
      parts.push(`${seconds}с`);
      setCountdown(parts.join(' '));
    };

    updateCountdown();
    intervalId = setInterval(updateCountdown, 1000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [nextWordTime, fetchTodayWord, applyTodayPayload, fingerprintFor]);

  const positionLabel = (position) => {
    if (!position) return '-';
    if (position === 1) return '1-е';
    if (position === 2) return '2-е';
    if (position === 3) return '3-е';
    return `${position}-е`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const guessTrimmed = guess.trim().toUpperCase();

    if (!guessTrimmed) {
      setMessage('Введите слово');
      return;
    }

    if (!/^[А-ЯЁ]+$/.test(guessTrimmed)) {
      setMessage('Используйте только русские буквы');
      return;
    }

    if (guessTrimmed.length < 9) {
      setMessage('Маловато букв');
      return;
    }
    if (guessTrimmed.length > 9) {
      setMessage('Многовато букв');
      return;
    }

    setMessage('');
    try {
      const response = await axios.post(`${WORD_API_URL}/game/guess`, {
        guess: guessTrimmed
      });

      if (response.data.correct) {
        setSolved(true);
        setSolvedTime(response.data.time);
        setCurrentWord(response.data.word);
        setLastPosition(response.data.position || 1);
        setLastScore(response.data.score ?? 0);
        setGuess('');
        setPickedIndices([]);
        setMessage('');
        setGuessShake(false);
      } else {
        setMessage(response.data.message || 'Мимо! Попробуй ещё раз.');
        setGuess('');
        setPickedIndices([]);
        if (shakeTimerRef.current) {
          window.clearTimeout(shakeTimerRef.current);
        }
        setGuessShake(false);
        // Retrigger shake even on consecutive misses.
        window.requestAnimationFrame(() => {
          setGuessShake(true);
          shakeTimerRef.current = window.setTimeout(() => setGuessShake(false), 450);
        });
        if (hasFinePointer()) {
          window.requestAnimationFrame(() => {
            guessInputRef.current?.focus();
          });
        }
      }
    } catch (error) {
      console.error('Error submitting guess:', error);
      setMessage(error.response?.data?.error || 'Ошибка при отправке ответа');
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds} сек`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes} мин ${secs} сек`;
  };

  const usedLetterIndices = useMemo(() => new Set(pickedIndices), [pickedIndices]);

  const dismissTapHint = () => {
    if (!showTapHint) return;
    setShowTapHint(false);
    markTapHintSeen();
  };

  const handleLetterClick = (index, letter) => {
    if (solved || !letter) return;
    dismissTapHint();
    const next = togglePick(pickedIndices, index);
    if (!next) return;
    setPickedIndices(next);
    setGuess(guessFromIndices(shuffledLetters, next));
    if (message) setMessage('');
  };

  const handleGuessChange = (value) => {
    const nextGuess = String(value || '').toUpperCase().slice(0, 9);
    setGuess(nextGuess);
    setPickedIndices(assignIndicesForGuess(shuffledLetters, nextGuess));
    if (message) setMessage('');
  };

  const handleBackspace = () => {
    if (!guess && pickedIndices.length === 0) return;
    if (pickedIndices.length > 0) {
      const next = pickedIndices.slice(0, -1);
      setPickedIndices(next);
      setGuess(guessFromIndices(shuffledLetters, next));
    } else {
      handleGuessChange(guess.slice(0, -1));
      return;
    }
    if (message) setMessage('');
  };

  if (loading) {
    return (
      <AppShell>
        <div className="loading">Загрузка...</div>
      </AppShell>
    );
  }

  const nextWordLabel = refreshingWord
    ? 'Обновляем слово…'
    : countdown
      ? `Следующее слово через ${countdown}`
      : 'Следующее слово скоро';

  return (
    <AppShell>
      <div className="game-content">
        <p className="game-kicker">#AlliancePubWord</p>

        <LetterGrid
          letters={shuffledLetters}
          size="md"
          usedIndices={solved ? null : usedLetterIndices}
          onLetterClick={solved ? null : handleLetterClick}
          hint={!solved && showTapHint}
        />
        {!solved && showTapHint && (
          <p className="tap-hint">Нажимай на буквы или печатай</p>
        )}

        {solved ? (
          <div className="panel solved-card">
            <h3>Есть! Засчитано</h3>
            <p className="solved-word">{currentWord}</p>
            <div className="solved-stats">
              {solvedTime !== null && (
                <div>
                  <span>От публикации</span>
                  <strong>{formatTime(solvedTime)}</strong>
                </div>
              )}
              {lastScore !== null && (
                <div>
                  <span>Счёт</span>
                  <strong>{lastScore}</strong>
                </div>
              )}
              {lastPosition && (
                <div>
                  <span>Место</span>
                  <strong>{positionLabel(lastPosition)}</strong>
                </div>
              )}
            </div>
            <p className="game-rule">
              Время считается от публикации слова — общая точка старта для всех.
            </p>
            <p className="next-word">{nextWordLabel}</p>
          </div>
        ) : (
          <GuessInput
            value={guess}
            length={9}
            onChange={handleGuessChange}
            onBackspace={handleBackspace}
            onSubmit={handleSubmit}
            inputRef={guessInputRef}
            shake={guessShake}
            message={message}
          >
            <p className="game-rule">
              Время считается от публикации слова — общая точка старта для всех.
            </p>
            {(countdown || refreshingWord) && (
              <p className="next-word">{nextWordLabel}</p>
            )}
          </GuessInput>
        )}
      </div>
    </AppShell>
  );
}

export default Game;
